import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import PropTypes from "prop-types";
import { z } from "zod";
import {
  signolImagesRepositoryURL,
  UsernameTypes,
  DEFAULT_LANGUAGE,
  URL_SIGNOL_PRIVACY_POLICY,
  EMAIL_SIGNOL_SUPPORT,
} from "@src/constants/constants";
import { AppUtils, useIsMobileWidth } from "@src/utils";
import AuthHooks from "@src/context/hooks/authHooks";
import { authEndpoints } from "@src/services/endpoints";
import { useToastContext } from "@src/context/toastContext";

import {
  Checkbox,
  ImageWithRetry,
  Button,
  FormInput,
} from "@src/components/atoms";
import Form from "./Form";
import {
  cognitoPasswordValidator,
  signupEmailSchema,
  signupUsernameSchema,
} from "./formHelpers";
import { COMMON_CONTROLS } from "@src/stories/utils/common-controls";

const SignupForm = ({
  onSignupSubmit,
  defaultValues,
  industry,
  enableBack = false,
  usernameType = UsernameTypes.USERNAME,
  customerCode,
  employeeNumberSettings,
  signupToken,
}) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(defaultValues ?? {});
  const [hasFilledStep1, setHasFilled] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMobile = useIsMobileWidth();
  const { showToast } = useToastContext();

  const isUsername = usernameType == UsernameTypes.USERNAME;

  const signupMutation = AuthHooks.useSignup(
    !isUsername,
    () => {
      console.log("success");
      showToast("Welcome to Signol", "success");
    },
    (error) => {
      console.error("error", error);
      setError(error.message);
      showToast(error.message, "error");
    },
  );

  useEffect(() => {
    const { firstname, lastname, username } = defaultValues ?? {};
    const allFieldsFilled =
      typeof firstname === "string" &&
      firstname.trim().length > 0 &&
      typeof lastname === "string" &&
      lastname.trim().length > 0 &&
      typeof username === "string" &&
      username.trim().length > 0;

    if (allFieldsFilled) {
      setHasFilled(true);
      setStep(2);
    }
  }, [defaultValues]);

  /** Step 1 (Users clicking signup links in emails will skip this step) */
  const FormFullNameAndUsername = () => {
    const handleNext = async (form) => {
      setError("");
      setIsSubmitting(true);
      const sanitisedUsername = !!Number(form.username.trim())
        ? String(Number(form.username)) // this removes 0 prefix
        : form.username.toLowerCase().replace(/-|\s/g, ""); // this removes dashes and whitespaces
      try {
        const verifyRes = await authEndpoints.verifyOperatorCode(
          sanitisedUsername,
          customerCode,
        );
        if (
          verifyRes === false ||
          verifyRes.result === false ||
          industry !== verifyRes.data?.industry
        ) {
          throw new Error(
            `Invalid ${employeeNumberSettings.label.toLowerCase()}. Please contact support at ${EMAIL_SIGNOL_SUPPORT}.`,
          );
        }
        setFormData((prev) => ({
          ...prev,
          ...form,
          username: sanitisedUsername,
        }));
        setIsSubmitting(false);
        setHasFilled(true);
        setStep(2);
      } catch (e) {
        setError(e.message);
        setIsSubmitting(false);
      }
    };

    return (
      <Form
        id={"signup-step1"}
        onSubmit={handleNext}
        schema={isUsername ? signupUsernameSchema : signupEmailSchema}
        options={{
          defaultValues: formData,
        }}
        className="space-y-6"
        loading={isSubmitting}
      >
        {(methods) => {
          console.log("errors", !!Object.keys(methods.formState.errors).length);

          return (
            <div className={`${isMobile ? "" : "px-6"}`}>
              <div
                className={`${isMobile ? "flex flex-col justify-center items-center py-6" : "grid grid-cols-2"} gap-6 mb-4`}
              >
                <FormInput
                  label="First Name"
                  name="firstname"
                  type="text"
                  placeholder="John"
                  textColor={"primary"}
                  error={methods.formState.errors.firstname?.message}
                />
                <FormInput
                  label="Last Name"
                  name="lastname"
                  type="text"
                  placeholder="Smith-Harris"
                  textColor={"primary"}
                  error={methods.formState.errors.lastname?.message}
                  required
                />
                <FormInput
                  placeholder={employeeNumberSettings?.example}
                  label={employeeNumberSettings?.label}
                  info={employeeNumberSettings?.tooltip}
                  name="username"
                  type={isUsername ? "text" : "email"}
                  textColor={"primary"}
                  error={methods.formState.errors.username?.message}
                  autoComplete="username"
                  required
                  className="data-hj-allow"
                />
              </div>
              <div
                className={`${!isMobile ? "flex justify-center items-center" : "flex flex-col gap-4 items-center"} relative  mt-6`}
              >
                <div className={`${isMobile && "w-full"}`}>
                  <Button padding={false} submit loading={isSubmitting}>
                    Next
                  </Button>
                </div>

                {!!enableBack && (
                  <div
                    className={`${
                      isMobile
                        ? "flex space-x-1 p-2 text-center mt-2"
                        : "absolute right-0 pr-4 flex space-x-1"
                    }`}
                  >
                    <span
                      className={`h-4 w-4 rounded-full bg-primary-400 cursor-pointer`}
                      onClick={() => setStep(1)}
                    ></span>
                    <span
                      className={`h-4 w-4 rounded-full bg-gray-200 cursor-pointer`} //animate-animated animate-pulse animate-infinite
                      onClick={hasFilledStep1 ? () => setStep(2) : null}
                    ></span>
                  </div>
                )}
              </div>
            </div>
          );
        }}
      </Form>
    );
  };

  /** Signup Step 2 (Users clicking signup links in emails will begin here) */
  const FormEnterNewPasswordAndAcceptTerms = () => {
    const handleSubmit = async (data) => {
      //flushSync forces react to re-render the DOM before continuing
      flushSync(() => {
        setError("");
        setIsSubmitting(true);
      });

      const finalData = { ...formData, ...data };
      signupMutation.mutate(
        {
          username: finalData.username,
          firstname: finalData.firstname,
          lastname: finalData.lastname,
          password: finalData.password,
          customerCode,
          roles: ["CAPTAIN"],
          metadata: {
            operatorCode: finalData.username,
            dataPrivacyAgreement: finalData.termsAccepted ? Date.now() : null,
            selfSignup: true,
            industry,
            language: DEFAULT_LANGUAGE,
          },
        },
        {
          onSuccess: () => {
            if (onSignupSubmit)
              onSignupSubmit({
                username: finalData.username,
                password: finalData.password,
              });
          },
          onSettled: () => {
            setIsSubmitting(false);
          },
        },
      );
    };
    const step2Schema = z.object({
      password: cognitoPasswordValidator,
      termsAccepted:
        industry === "aviation"
          ? z.literal(true, {
              errorMap: () => ({
                message: "You must accept the privacy policy",
              }),
            })
          : z.optional(z.boolean()),
    });
    return (
      <Form
        id={"signup-step2"}
        onSubmit={handleSubmit}
        schema={step2Schema}
        className="space-y-6"
        loading={isSubmitting}
      >
        {(methods) => {
          return (
            <div className={`flex flex-col  gap-4 ${isMobile ? "" : "px-6"}`}>
              <div>
                <FormInput
                  placeholder={employeeNumberSettings?.example}
                  label={employeeNumberSettings?.label}
                  info={employeeNumberSettings?.tooltip}
                  name="username"
                  disabled
                  type={isUsername ? "text" : "email"}
                  textColor={"primary"}
                  fValue={formData?.username}
                  autoComplete="off"
                  className="data-hj-allow"
                />
                {signupToken && (
                  <p className="mt-1 text-gray-400 text-xs">
                    Is this not you?{" "}
                    <a
                      href={`/crewUpdate?t=${signupToken}`}
                      className="underline text-primary-500 hover:text-primary-700"
                    >
                      Let us know
                    </a>
                  </p>
                )}
              </div>
              <FormInput
                label="Password"
                name="password"
                type="password"
                placeholder="••••••••"
                showPasswordRules={true}
                textColor={"primary"}
                validationMode="onChange"
                error={methods.formState.errors.password?.message}
                autoComplete="new-password"
              />
              {industry === "aviation" && (
                <div className="w-full my-6 text-sm text-left text-neutral-800">
                  <p>
                    Signol is committed to protecting your privacy and will only
                    store and process your information to provide the Signol
                    service, as outlined in our privacy policy.
                  </p>
                  <Checkbox
                    id="termsAccepted"
                    name="termsAccepted"
                    maxWidthMd={false}
                    type="other"
                    label={
                      <div className="text-sm">
                        I have read and understand the Signol{" "}
                        <a
                          href={URL_SIGNOL_PRIVACY_POLICY}
                          target="_blank"
                          className="text-neutral-800 underline"
                        >
                          privacy policy
                        </a>
                        .
                      </div>
                    }
                    checked={methods.watch("termsAccepted")}
                    onChange={(e) => {
                      methods.setValue("termsAccepted", e.target.checked);
                    }}
                    error={methods.formState.errors.termsAccepted?.message}
                  />
                </div>
              )}
              <div
                className={`${!isMobile ? "flex justify-center items-center" : "flex flex-col gap-4 items-center"} relative  mt-6`}
              >
                <div className={`${isMobile && "w-full"}`}>
                  <Button padding={false} submit loading={isSubmitting}>
                    Sign Up
                  </Button>
                </div>

                {!!enableBack && (
                  <div
                    className={`${
                      isMobile
                        ? "flex space-x-1 p-2 text-center mt-2"
                        : "absolute right-0 pr-4 flex space-x-1"
                    }`}
                  >
                    <span
                      className={`h-4 w-4 rounded-full ${step === 1 ? "bg-primary-400" : "bg-gray-200"} cursor-pointer`}
                      onClick={() => setStep(1)}
                    ></span>
                    <span
                      className={`h-4 w-4 rounded-full ${step === 2 ? "bg-primary-400" : "bg-gray-200"} cursor-pointer `} //animate-animated animate-pulse animate-infinite
                      onClick={hasFilledStep1 ? () => setStep(2) : null}
                    ></span>
                  </div>
                )}
              </div>
            </div>
          );
        }}
      </Form>
    );
  };

  return (
    <div className=" mx-auto p-6">
      <div
        className={`flex ${isMobile ? "flex-col-reverse gap-4 mb-4" : `${customerCode ? "justify-between" : "justify-center"} mb-8 px-6`} items-start`}
      >
        <div className={`mb-6 ${customerCode ? "text-left" : "text-center"}`}>
          <h3 className=" mb-4 text-primary-500">
            Register for an online account
          </h3>
          <p>Please complete all the required fields.</p>
        </div>
        {customerCode && (
          <div className=" py-2 w-40">
            <ImageWithRetry
              src={
                !!customerCode
                  ? `${signolImagesRepositoryURL}/customers/${customerCode}/logo_${customerCode}_customer.png`
                  : `${signolImagesRepositoryURL}/SignolLogoPrimary.png`
              }
              alt="Logo"
            />
          </div>
        )}
      </div>
      {step === 1 && FormFullNameAndUsername()}
      {step === 2 && FormEnterNewPasswordAndAcceptTerms()}
      {error && <div className="py-6 text-error">{error}</div>}
    </div>
  );
};

export const signupFormControls = {
  onSignupSubmit: {
    propType: PropTypes.func.isRequired,
    description: "Form submission handler from parent",
    disable: true,
  },
  industry: COMMON_CONTROLS.industry,
  customerCode: COMMON_CONTROLS.customerCode,
  enableBack: {
    propType: PropTypes.bool,
  },
  signupToken: {
    propType: PropTypes.string,
  },
};

SignupForm.propTypes = AppUtils.getPropTypeFromControls(signupFormControls);

export default SignupForm;
